import { MinzeElement, Attrs, Reactive, EventListeners } from 'minze'
import { Locale } from '../../modules/locale'
import { FavoriteItem, FavoriteItemType } from '../../modules/auth'
import { mapEvent } from '../../modules/utils'
import { LvcItemSourceData } from '../../../types/global'
import { encodeSlides } from '../../modules/sliderUtils'
import { TabItem } from '../lvc-tabs'
import MainModule from '../../modules/main'

Locale.getInstance().registerKeySet('fr', 'lvcAccountFavorites', {
  title: 'Mes favoris',
  description: 'Indiquez vos favoris pour personnaliser votre expérience sur La Vitrine!',
  tabs: {
    contributors: 'Artistes',
    events: 'Événements',
    eventSeries: 'Festivals et séries',
    exhibitions: 'Expositions',
    places: 'Lieux',
  },
  permanentExhibitions: 'Expositions permanentes',
  eventsCount: {
    plural: 'événements',
    singular: 'événement',
  },
  exhibitionsCount: {
    plural: 'expositions',
    singular: 'exposition',
  },
  noFavoriteContributorsTitle: "Vous n'avez pas d'artiste favori",
  noFavoriteContributorsDescription: 'Pour ajouter un artiste à votre liste, cliquez sur Favoris',
  noFavoriteEventsTitle: "Vous n'avez pas d'événements favoris",
  noFavoriteEventsDescription: 'Pour ajouter un événement à votre liste, cliquez sur Favoris',
  noFavoriteEventSeriesTitle: "Vous n'avez pas de festival ou de série favoris",
  noFavoriteEventSeriesDescription:
    'Pour ajouter un festival ou une série à votre liste de favoris, cliquez sur Favoris',
  noFavoriteExhibitionsTitle: "Vous n'avez pas d'expositions favorites",
  noFavoriteExhibitionsDescription:
    'Pour ajouter une exposition à votre liste, cliquez sur Favoris',
  noFavoritePlacesTitle: "Vous n'avez pas de lieu favori",
  noFavoritePlacesDescription: 'Pour ajouter un lieu à votre liste, cliquez sur Favoris',
})

Locale.getInstance().registerKeySet('en', 'lvcAccountFavorites', {
  title: 'My favorites',
  description: 'Share your favorites to personalize your experience on La Vitrine!',
  tabs: {
    contributors: 'Artists',
    events: 'Events',
    eventSeries: 'Festivals and series',
    exhibitions: 'Exhibitions',
    places: 'Places',
  },
  permanentExhibitions: 'Permanent exhibitions',
  eventsCount: {
    plural: 'events',
    singular: 'event',
  },
  exhibitionsCount: {
    plural: 'exhibitions',
    singular: 'exhibition',
  },
  noFavoriteContributorsTitle: 'You have no favorite artists',
  noFavoriteContributorsDescription: 'Click on Favorite to add an artist to your list',
  noFavoriteEventsTitle: 'You have no favorite events',
  noFavoriteEventsDescription: 'Click on Favorite to add an event to your list',
  noFavoriteEventSeriesTitle: 'You have no favorite festivals or series',
  noFavoriteEventSeriesDescription: 'Click on Favorite to add a festival or serie to your list',
  noFavoriteExhibitionsTitle: 'You have no favorite exhibitions',
  noFavoriteExhibitionsDescription: 'Click on Favorite to add an exhibition to your list',
  noFavoritePlacesTitle: 'You have no favorite places',
  noFavoritePlacesDescription: 'Click on Favorite to add a place to your list',
})

export interface LvcAccountFavorites {
  titleSeparator: boolean
  data: TabItem[]
  loading: boolean
  noFavorites: boolean
}

export class LvcAccountFavorites extends MinzeElement {
  attrs: Attrs = [['title-separator', false]]
  static observedAttributes = ['titleSeparator']
  reactive: Reactive = [
    ['data', null],
    ['loading', false],
    ['noFavorites', false],
  ]

  updateFavoritesData = (event: Event) => {
    const detail = (event as CustomEvent).detail
    const favorites: FavoriteItem[] = detail.favorites
    MainModule.getInstance().loadFavoritesItems(favorites)
  }

  onFavoritesLoaded = async () => {
    if (!this.shadowRoot) return
    await this.renderFavoriteItems()
  }

  getNoFavoriteText = (type: string, textName: string) => {
    switch (type) {
      case FavoriteItemType.Contributors:
        return Locale.getKey('lvcAccountFavorites.noFavoriteContributors' + textName)
      case FavoriteItemType.Event:
        return Locale.getKey('lvcAccountFavorites.noFavoriteEvents' + textName)
      case FavoriteItemType.EventSeries:
        return Locale.getKey('lvcAccountFavorites.noFavoriteEventSeries' + textName)
      case FavoriteItemType.Exhibition:
        return Locale.getKey('lvcAccountFavorites.noFavoriteExhibitions' + textName)
      case FavoriteItemType.Places:
        return Locale.getKey('lvcAccountFavorites.noFavoritePlaces' + textName)
      default:
        return ''
    }
  }

  renderNoFavorites = (type: string) => {
    return /* html */ `
      <div class="no-favorites-warning">
        <div class="no-favorites-warning-image">
          <lvc-charlie-waving-icon
            width="${window.matchMedia('(max-width: 730px)').matches ? '100%' : ''}"
            height="auto"
          ></lvc-charlie-waving-icon>
        </div>
        <div class="no-favorites-warning-content">
          <h2 class="no-favorites-warning-title">${this.getNoFavoriteText(type, 'Title')}</h2>
        </div>
      </div>
    `
  }

  renderFavoriteItems = async () => {
    this.loading = true

    const detail = (event as CustomEvent).detail
    const sortOrder = ['contributors', 'places', 'exhibitions', 'events', 'eventSeries']
    let packData: any[] = []

    if (detail?.error) {
      this.noFavorites = true
      return
    }

    for (let [key, value] of Object.entries(detail.data)) {
      let type = key
      if (key === 'allEvents') continue
      if (key === 'contributors') type = 'contributors'
      if (key === 'eventOffers') type = 'events'
      if (key === 'exhibitionEvents') type = 'exhibitions'

      const items = (value as { data: any[] }).data
      const title = Locale.getKey(`lvcAccountFavorites.tabs.${type}`)
      let content = ''

      if (items.length > 0) {
        if (type === 'contributors' || type === 'places') {
          content = /* html */ `
            <lvc-account-favorites-grid
              data="${encodeURIComponent(JSON.stringify(items))}"
              grid-template="1fr 1fr 1fr"
            ></lvc-account-favorites-grid>
          `
        } else {
          const parsedItems = (items as LvcItemSourceData[]).map(item => mapEvent(item))
          content = /* html */ `
            <lvc-grid-layout
              data-component-location="favoris"
              data="${encodeSlides(parsedItems)}"
              grid-template="1fr 1fr 1fr"
              showcase-mode="false"
            ></lvc-grid-layout>
          `
        }
      } else {
        content = `<lvc-account-favorites-empty type="${type}"></lvc-account-favorites-empty>`
      }

      packData.push({
        type: type,
        title: title,
        tabContent: content,
        total: items.length,
      })
    }

    this.data = packData.sort((a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type))
    this.loading = false
  }

  html = () => `
    <div class="account-favorites">
      <lvc-account-header
        label="${Locale.getKey('lvcAccountFavorites.title')}"
        description="${Locale.getKey('lvcAccountFavorites.description')}"
        separator="${this.titleSeparator}"
      >
        <lvc-account-heart-icon width="44" height="44" slot="icon"></lvc-account-heart-icon>
      </lvc-account-header>

      ${
        this.data && !this.loading
          ? /* html */ `
        <lvc-tab-group
          class="${this.loading ? 'loading' : ''}"
          global-padding="false"
          mobile-behaviour="toggle"
          items="${encodeURIComponent(JSON.stringify(this.data))}"
        ></lvc-tab-group>
      `
          : /* html */ `
        <lvc-tab-group-loader class="loading"></lvc-tab-group-loader>
      `
      }
    </div>
  `

  eventListeners: EventListeners = [
    [window, 'lvc:user-data-updated', this.updateFavoritesData],
    [window, 'lvc:item-favorite-status-update', this.updateFavoritesData],
    [window, 'lvc:favorites-items-loaded', this.onFavoritesLoaded],
    [window, 'lvc:favorites-items-loading-failed', this.onFavoritesLoaded],
  ]

  css = () => `
    :host {}

    .account-favorites-tabs {
      display: flex;
      gap: 30px;
      width: 100%;
      border-bottom: 1px solid #1E007D;
    }

    .account-favorites-tab {
      border: none;
      background: transparent;
      padding: 18px 0px;
      color: #1E007D;
      font-size: 1.125em;
      font-weight: 500;
      border-bottom: 4px solid transparent;
      cursor: pointer;
      font-family: inherit;
      border-bottom: 4px solid transparent;
      transition: all 0.2s;
    }

    .account-favorites-tab.active {
      border-bottom: 4px solid #1E007D;
    }

    .account-favorites-tab.active:hover {
      border-bottom: 4px solid #1E007D;
    }

    .account-favorites-tab:hover {
      border-bottom: 4px solid rgba(30, 0, 125, 0.2);
    }

    .no-favorites-warning {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .no-favorites-warning-image {}

    .no-favorites-warning-content {}

    .no-favorites-warning-title {
      color: #1E007D;
      font-size: 1.625em;
      font-weight: 800;
    }
    
    @media screen and (max-width: 991px) {
      
    }

    @media only screen and (min-width: 992px) {
      
    }
  `
}
